/**
 * Client-side session state — NOT the user record.
 *
 * The signed-in user lives in the TanStack Query cache and nowhere else; putting
 * a copy here would create a second source of truth that wins every disagreement
 * by being wrong. What this store holds is the two things the cache cannot:
 *
 *   status       flipped to `signed_out` by the 401 handler, which runs OUTSIDE
 *                React (see ../lib/unauthenticated) and therefore has no hook to
 *                write to. This is what auto-logs-out the app after five idle
 *                days (US-00-001.15).
 *   destination  the screen the person was trying to reach when the session was
 *                found to be missing — a WhatsApp deep link, or the route they
 *                were already on. Held across the OTP round trip and DISCARDED
 *                AFTER ONE USE (US-00-001.20, US-00-002.21, US-00-002.26).
 *
 * The durable copy of `destination` is in @anthra/platform/session, which
 * survives a full page load; this is the in-memory mirror the router reads.
 */

import { create } from "zustand"

export type SessionStatus = "unknown" | "authenticated" | "signed_out"

type SessionState = {
	status: SessionStatus
	destination: string | null
	markAuthenticated: () => void
	markSignedOut: () => void
	setDestination: (path: string | null) => void
	/** Reads and clears in one call, so a destination cannot be used twice. */
	consumeDestination: () => string | null
}

export const useSessionStore = create<SessionState>()((set, get) => ({
	status: "unknown",
	destination: null,

	markAuthenticated: () => {
		set({ status: "authenticated" })
	},

	markSignedOut: () => {
		set({ status: "signed_out" })
	},

	setDestination: path => {
		set({ destination: path })
	},

	consumeDestination: () => {
		const { destination } = get()
		if (destination !== null) set({ destination: null })
		return destination
	},
}))
